import { AdventDoor, AdventDoorMechanic } from "apps/user/learner/api/types";

/**
 * A door's mechanic with its config, and the parts of the learner's saved
 * response the sheet reads back, parsed out of the API's JSON strings.
 */
export type DoorMechanic =
  | { kind: "NONE" }
  | { kind: "DONE" }
  | { kind: "WRITE"; questions: string[]; answers: string[] | null }
  | { kind: "TIMER"; minutes: number }
  | {
      kind: "BREATH";
      breaths: number;
      inhaleSeconds: number;
      exhaleSeconds: number;
    }
  | {
      kind: "CHOICE";
      options: string[];
      allowOther: boolean;
      choice: string | null;
    };

/** The `response` argument of `completeAdventDoor`, by mechanic. */
export type DoorMechanicResponse =
  | { answers: string[] }
  | { seconds: number }
  | { breaths: number }
  | { choice: string };

type JsonObject = Record<string, unknown>;

class MalformedMechanicError extends Error {
  constructor(door: AdventDoor, problem: string) {
    super(`Door ${door.dayNumber} (${door.mechanic}): ${problem}`);
    this.name = "MalformedMechanicError";
  }
}

const parseObject = (
  door: AdventDoor,
  field: string,
  text: string
): JsonObject => {
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch {
    // Never echo the text: a response holds what the learner wrote, and this
    // error reaches Sentry.
    throw new MalformedMechanicError(door, `${field} is not JSON`);
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new MalformedMechanicError(door, `${field} is not an object`);
  }
  return value as JsonObject;
};

const isStringList = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

const positiveInteger = (
  door: AdventDoor,
  config: JsonObject,
  key: string
): number => {
  const value = config[key];
  if (!isPositiveInteger(value)) {
    throw new MalformedMechanicError(
      door,
      `"${key}" must be a positive integer`
    );
  }
  return value;
};

const nonEmptyStrings = (
  door: AdventDoor,
  config: JsonObject,
  key: string
): string[] => {
  const value = config[key];
  if (!isStringList(value) || value.length === 0) {
    throw new MalformedMechanicError(door, `"${key}" must list strings`);
  }
  return value;
};

/** The saved response, or null before the first completion. */
const savedResponse = (door: AdventDoor): JsonObject | null =>
  door.response ? parseObject(door, "response", door.response) : null;

const parseWrite = (door: AdventDoor, config: JsonObject): DoorMechanic => {
  const questions = nonEmptyStrings(door, config, "questions");
  const response = savedResponse(door);
  if (response === null) return { kind: "WRITE", questions, answers: null };
  const { answers } = response;
  if (!isStringList(answers) || answers.length !== questions.length) {
    throw new MalformedMechanicError(
      door,
      `"answers" must hold one string per question`
    );
  }
  return { kind: "WRITE", questions, answers };
};

const parseChoice = (door: AdventDoor, config: JsonObject): DoorMechanic => {
  const options = nonEmptyStrings(door, config, "options");
  const allowOther = config.allow_other ?? false;
  if (typeof allowOther !== "boolean") {
    throw new MalformedMechanicError(door, `"allow_other" must be a boolean`);
  }
  const response = savedResponse(door);
  if (response === null) {
    return { kind: "CHOICE", options, allowOther, choice: null };
  }
  const { choice } = response;
  if (typeof choice !== "string") {
    throw new MalformedMechanicError(door, `"choice" must be a string`);
  }
  return { kind: "CHOICE", options, allowOther, choice };
};

/** A saved counted response ({seconds} or {breaths}) must be a positive
 * whole number; the value itself isn't shown, only that it was done. */
const checkCountedResponse = (door: AdventDoor, key: string): void => {
  const response = savedResponse(door);
  if (response !== null && !isPositiveInteger(response[key])) {
    throw new MalformedMechanicError(
      door,
      `"${key}" must be a positive integer`
    );
  }
};

const PARSERS: Record<
  AdventDoorMechanic,
  (door: AdventDoor, config: JsonObject) => DoorMechanic
> = {
  NONE: () => ({ kind: "NONE" }),
  DONE: () => ({ kind: "DONE" }),
  WRITE: parseWrite,
  TIMER: (door, config) => {
    checkCountedResponse(door, "seconds");
    return { kind: "TIMER", minutes: positiveInteger(door, config, "minutes") };
  },
  BREATH: (door, config) => {
    checkCountedResponse(door, "breaths");
    return {
      kind: "BREATH",
      breaths: positiveInteger(door, config, "breaths"),
      inhaleSeconds: positiveInteger(door, config, "inhale_seconds"),
      exhaleSeconds: positiveInteger(door, config, "exhale_seconds"),
    };
  },
  CHOICE: parseChoice,
};

/**
 * Throws on a mechanic this client does not know or a config that does not
 * match it: a door rendered from a guessed shape would record nonsense.
 */
export const parseDoorMechanic = (door: AdventDoor): DoorMechanic => {
  const parse = PARSERS[door.mechanic];
  if (!parse) {
    throw new MalformedMechanicError(door, "unknown mechanic");
  }
  return parse(door, parseObject(door, "mechanicConfig", door.mechanicConfig));
};

export const parseDoorMechanics = (
  doors: AdventDoor[]
): ReadonlyMap<string, DoorMechanic> =>
  new Map(doors.map((door) => [door.id, parseDoorMechanic(door)]));

/** The instant a door was first completed, as "12 December" for the learner. */
export const formatCompletedDate = (
  completedAt: string,
  language: string
): string =>
  new Intl.DateTimeFormat(language, { day: "numeric", month: "long" }).format(
    new Date(completedAt)
  );

/** Whole seconds as "mm:ss". */
export const formatClock = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
    2,
    "0"
  )}`;
};
